/**
 * Date utility functions for chart labels
 * Ensures consistent, readable date formatting across all WHOOP and Garmin charts
 * 
 * IMPORTANT DATE HANDLING BY DATA TYPE:
 * 
 * 1. SLEEP DATA FROM OAUTH API - Do NOT shift
 *    - WHOOP OAuth API returns `created_at` which is already the wake-up date
 *    - Example: sleep from night of Jan 26-27 is returned with date "Jan 27" (correct)
 *    - No shift needed - already represents the correct day
 * 
 * 2. SLEEP DATA FROM DATABASE MODELS - Use shiftDateForward()
 *    - Internal WearableDevice SleepData is stored with the sleep START date
 *    - Example: sleep from night of Jan 26-27 is stored as "Jan 26"
 *    - We shift forward by 1 day to display under the WAKE-UP date (Jan 27)
 *    - Only used when building date maps from combined sources
 * 
 * 3. RECOVERY DATA - Do NOT shift
 *    - Recovery timestamp is when the score was CALCULATED (morning after waking up)
 *    - Example: recovery calculated Jan 27 morning is stored as "Jan 27"
 *    - Already represents the correct day - no shift needed
 * 
 * 4. ACTIVITY/STRAIN DATA - Do NOT shift
 *    - Activity timestamp is when the workout/activity STARTED
 *    - Workouts happen during the day, not overnight
 *    - Already represents the correct day - no shift needed
 */

/**
 * Generate a date range array for consistent chart x-axis labels
 * Creates an array of ISO date strings (YYYY-MM-DD) for the last N days ending at TODAY
 * 
 * @param numDays - Number of days to generate (default: 30)
 * @returns Array of ISO date strings in ascending order, ending at today
 */
export const generateDateRange = (numDays: number): string[] => {
  const results: string[] = [];
  for (let i = numDays - 1; i >= 0; i--) {
    const d = new Date();
    d.setHours(12, 0, 0, 0);  // Use noon to avoid any DST edge cases
    d.setDate(d.getDate() - i);
    // Format as YYYY-MM-DD
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    results.push(`${year}-${month}-${day}`);
  }
  return results;
};

/**
 * Shift a date string forward by 1 day
 * 
 * USE ONLY FOR DATABASE SLEEP DATA - when building date maps from combined data sources
 * 
 * DO NOT use for WHOOP OAuth API data - that already has the correct wake-up date
 * DO NOT use for single-source charts that just display API data as-is
 * 
 * Example use case: Combined chart merging WHOOP OAuth API + Garmin data
 * - WHOOP OAuth: date is already correct (wake-up date)
 * - Garmin internal: stored as sleep start date, needs shifting
 * - This function shifts Garmin data to align with WHOOP dates
 * 
 * @param dateStr - Date string in ISO format (YYYY-MM-DD)
 * @returns Date string shifted forward by 1 day
 */
export const shiftDateForward = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00Z');
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().split('T')[0];
};

/**
 * Format date labels for chart display.
 * Includes the weekday so coaches can cross-reference dates against
 * their weekly training structure without counting backwards.
 *
 * @param label - Date string in ISO format (YYYY-MM-DD) or already formatted
 * @returns Formatted date string like "Thu Nov 8"
 */
export const formatDateLabel = (label: string): string => {
  try {
    // If it's an ISO date (YYYY-MM-DD), format it
    // Use noon local time to avoid timezone issues (UTC midnight shows as previous day in western timezones)
    if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
      const d = new Date(label + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    // If it's already formatted with weekday (e.g., "Thu, Nov 8" or "Thu Nov 8"), return as is.
    // en-US toLocaleDateString with { weekday: 'short', ... } emits a comma after the weekday
    // in modern engines, so we allow an optional comma for idempotency.
    if (/^[A-Za-z]{3},?\s+[A-Za-z]{3}\s+\d{1,2}$/.test(label)) {
      return label;
    }
    // If it's legacy "Nov 8" format, prepend the inferred weekday when parseable
    const date = new Date(label);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  } catch (e) {
    // Fallback to original label
  }
  return label;
};

/**
 * Format date for chart labels
 * Ensures consistent, readable date formatting across all charts
 *
 * @param dateStr - Date string in ISO format or other parseable format
 * @returns Formatted date string like "Nov 8"
 */
export const formatChartDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return dateStr;
  }
};

/**
 * Generate unique date labels for a date range
 * Prevents duplicate labels on X-axis
 *
 * @param dates - Array of date strings
 * @returns Array of unique formatted date labels
 */
export const generateUniqueDateLabels = (dates: string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];

  dates.forEach(date => {
    const formatted = formatChartDate(date);
    let label = formatted;
    let counter = 1;

    // If duplicate, append counter
    while (seen.has(label)) {
      label = `${formatted} (${counter++})`;
    }

    seen.add(label);
    unique.push(label);
  });

  return unique;
};

/**
 * Format date with day shift for recovery data
 * Recovery data from last night should show under today's date
 *
 * @param dateStr - Date string in ISO format
 * @returns Formatted date string shifted by one day
 */
export const formatRecoveryChartDate = (dateStr: string): string => {
  try {
    const displayDate = new Date(dateStr + 'T00:00:00Z');
    // Shift forward one day to show recovery under the day it applies to
    displayDate.setUTCDate(displayDate.getUTCDate() + 1);

    return displayDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return formatChartDate(dateStr);
  }
};
